import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Inbox, Send, Check, X, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, Empty, Badge, REQ_META, fmtTime } from "@/pages/portal/event-space/shared";
import { PersonCard, RequestModal } from "@/pages/portal/event-space/Participants";

export default function Networking({ space, slug, reload }) {
  const [tab, setTab] = useState("suggestions");
  const [reqs, setReqs] = useState([]);
  const [sugg, setSugg] = useState([]);
  const [req, setReq] = useState(null);
  const load = useCallback(() => {
    api.get(`/event-space/${slug}/requests`).then((r) => setReqs(r.data)).catch(() => {});
    if (space.can_network) api.get(`/event-space/${slug}/participants`).then((r) => setSugg((r.data.items || []).filter((p) => !p.request_status).slice(0, 9))).catch(() => {});
  }, [slug, space.can_network]);
  useEffect(() => { load(); }, [load]);

  const act = async (r, action) => {
    if (action === "cancel" && !window.confirm(`Annuler la rencontre avec ${r.direction === "outgoing" ? r.target_name : r.requester_name} ?`)) return;
    try {
      const res = await api.post(`/event-space/${slug}/requests/${r.id}/${action}`);
      const s = res.data.status;
      toast.success(s === "confirmed" ? `Rendez-vous confirmé à ${fmtTime(res.data.slot_start)}` : s === "accepted" ? "Accepté — aucun créneau commun pour l'instant, ouvrez vos disponibilités" : s === "declined" ? "Demande refusée" : "Demande annulée");
      load(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  if (!space.can_network) return <Empty testId="networking-locked">{space.has_grid ? "Le networking s'active quand votre inscription est validée et payée." : "Le matchmaking n'est pas activé pour cet événement."}</Empty>;
  const incoming = reqs.filter((r) => r.direction === "incoming");
  const outgoing = reqs.filter((r) => r.direction === "outgoing");
  const TABS = [["suggestions", "Découvrir", Sparkles, sugg.length], ["incoming", "Demandes reçues", Inbox, incoming.filter((r) => r.status === "pending").length], ["outgoing", "Demandes envoyées", Send, outgoing.length]];
  const list = tab === "incoming" ? incoming : outgoing;

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-3 text-xs text-neutral-600">
        {[["1. Découvrir", "Suggestions calculées d'après vos tags, objectifs et disponibilités."], ["2. Demander un match", "Votre demande reste « En attente » jusqu'à la réponse — aucun créneau n'est bloqué."], ["3. Planifier", "Une fois acceptée, le premier créneau commun est attribué et ajouté aux deux agendas."]].map(([t, d]) => <div key={t} className="rounded-xl bg-wine-50/60 p-3"><div className="font-bold text-navy">{t}</div>{d}</div>)}
      </div>
      <div className="flex gap-1 rounded-2xl bg-neutral-100 p-1">
        {TABS.map(([id, l, Icon, n]) => <button key={id} onClick={() => setTab(id)} className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold ${tab === id ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid={`networking-tab-${id}`}><Icon className="h-4 w-4" />{l}{n > 0 && <span className="rounded-full bg-wine-500 px-1.5 text-[10px] text-white">{n}</span>}</button>)}
      </div>
      {tab === "suggestions" ? (
        sugg.length === 0 ? <Empty testId="networking-no-suggestions">Aucune suggestion pour l'instant — complétez vos tags.</Empty> : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{sugg.map((p) => <PersonCard key={p.user_id} p={p} canNetwork onOpen={() => setReq(p)} onFav={() => api.post(`/event-space/${slug}/favorites/${p.user_id}`).then(load)} onRequest={() => setReq(p)} />)}</div>
        )
      ) : (
        <Card testId={`networking-list-${tab}`}>
          {list.length === 0 ? <Empty>Aucune demande.</Empty> : (
            <ul className="divide-y divide-neutral-100">
              {list.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`request-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-navy">{r.direction === "outgoing" ? r.target_name : r.requester_name}</div>
                    {r.message && <p className="text-xs italic text-neutral-500">« {r.message} »</p>}
                    {r.slot_start && <p className="text-xs font-semibold text-emerald-700">{fmtTime(r.slot_start)} · {r.location}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge meta={REQ_META[r.status]} testId={`request-status-${r.id}`} />
                    {r.direction === "incoming" && r.status === "pending" && <>
                      <button onClick={() => act(r, "accept")} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white" data-testid={`request-accept-${r.id}`}><Check className="h-3.5 w-3.5" />Accepter</button>
                      <button onClick={() => act(r, "decline")} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1 text-xs font-bold text-neutral-600" data-testid={`request-decline-${r.id}`}><X className="h-3.5 w-3.5" />Refuser</button>
                    </>}
                    {["pending", "accepted", "confirmed"].includes(r.status) && !(r.direction === "incoming" && r.status === "pending") && <button onClick={() => act(r, "cancel")} className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50" data-testid={`request-cancel-${r.id}`}><Ban className="h-3.5 w-3.5" />Annuler</button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
      {req && <RequestModal slug={slug} person={req} onClose={() => setReq(null)} onSent={() => { load(); reload(); }} />}
    </div>
  );
}
